import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";
import { dispatchToast, getCSRFToken, processResponseTriggers } from "../shared/htmx-helpers";
import { saveImageCredit, type CreditImage } from "./image-credits";
import { savePanoOverride, savePanoCrop } from "./pano-override";
import { openPanoChooser, getChosenPose, teardownPanoChooser, resizePanoChooser } from "./pano-crop";
import { loadTourGraph, saveTourGraph, type TourGraph, type TourNode } from "./tour-api";
import { mountLinkPane, type LinkPane, type PanoMarker } from "./tour-link-editor";
import { studioConfirm } from "../shared/studio-confirm";

// Field-for-field identical to pano-editor.ts's own PLACEMENT_MARKER_ID —
// duplicated rather than imported because pano-editor is a standalone
// bundle loaded via a <script> tag (see tour-link-editor.ts's top-of-file
// comment), not a module this file can import from. Used to seed the
// EXISTING arrow of an edit-mode connection under the SAME id the
// placement click handler upserts under, so the first click on an existing
// link replaces this seed instead of leaving it as a stale duplicate
// beside the freshly-placed marker (S4).
const PLACEMENT_MARKER_ID = "__placement__";

// PanoDraft is the reactive form state cmsPanoFields binds to (media-kind
// override sub-menu + frame-in-viewer crop chooser). yaw/pitch (DEGREES) are
// the saved poster/orientation framing, seeded in editPano() from the pool
// entry's pano_initial_yaw/pitch so the chooser re-opens at the last-chosen
// view; undefined when never chosen (chooser opens forward-facing).
interface PanoDraft {
  id: string;
  url: string;
  poster_url: string;
  media_kind: string;
  yaw?: number;
  pitch?: number;
  // media-04: carried from the pool entry so the takeover shows the
  // placeholder instead of the raw url and never mounts the crop chooser.
  processing_error?: string;
  width?: number;
  height?: number;
}

interface PoolImage {
  id: string;
  url?: string;
  // The optimized (≤8192px WebP) rendition the deploy pipeline serves as the
  // tour panorama. For a pano, `url` can be the raw equirect (too large for
  // mobile GPUs → "panorama cannot be loaded", and discarded from R2 after the
  // viewer-equirect repoint), so the 360° panes/chooser must prefer this — the
  // same source the public tour uses (website_deploy_assembly.go panorama).
  optimized_url?: string;
  label?: string;
  property_slug?: string;
  // Non-empty for an area-guide pool row (empty property_slug in that case).
  // Absent/empty on property and genuine site-wide rows.
  area_slug?: string;
  // True when the website carries a photo-credit override for this image
  // (server-stamped by websitePickerPoolJSON's MarkCMSImageCredits, Task 9).
  // Drives the tile's © button accent state. Absent → treated false.
  hasCredit?: boolean;
  // The credit fields themselves, present only when hasCredit is true
  // (server omits them via omitempty otherwise) — editCredit() below reads
  // these to prefill creditDraft.
  author?: string;
  license?: string;
  source_url?: string;
  title?: string;
  // Human display label for this image's scope (area name / accommodation
  // name). Server-stamped by the area picker assembly (handler_cms_editor.go)
  // ALONGSIDE property_slug/area_slug; "" (absent) on site-wide rows and on
  // every non-area picker's pool. Drives the per-entity scope dropdown's option
  // labels (scopeOptions) — the picker derives the whole dropdown from the pool.
  scope_name?: string;
  // True while this image's scene analysis is still in flight (server-set by
  // the CMS editor handler's batched status lookup). Drives the tile's
  // "analysing…" hint. Absent on legacy/just-uploaded entries → treated false.
  analyzing?: boolean;
  // Human/semantic text from the scene-analysis pipeline — what the search
  // actually matches (the id is a UUID, useless to a human).
  scene_description?: string;
  scene_type?: string;
  primary_function?: string;
  // Precomputed, lowercased language-agnostic search blob (server-built by
  // BuildCMSImageList): caption + alt + scene metadata + EN/DE enum synonyms.
  // Populated when the server sends the augmented pool; absent for legacy/raw entries.
  search?: string;
  // True for an archived-but-referenced pool row (server-set by BuildCMSImageList
  // / FilterCMSCandidates): the image was removed from the canonical pool but
  // survives here because this section still references it. Drives the tile's
  // archived info marker. Absent on normal entries → treated false.
  archived?: boolean;
  // "pano" for a 360-degree panorama, absent/"" for an ordinary photo (server
  // omits the key via omitempty). Drives the tile's two-state 360° trigger
  // and its poster-vs-equirect thumbnail source (360-panorama-gallery, Task 8).
  media_kind?: string;
  // Public URL of the rectilinear tile poster for a pano; absent for a photo
  // or a pano still mid-extraction (poster not generated yet).
  pano_poster_url?: string;
  // The saved poster/orientation framing (DEGREES), emitted by the CMS pool
  // JSON (cms_image_list.go's CMSImage/poolEntry, from domain PanoPosterYaw/
  // Pitch — omitempty, same pattern as media_kind/pano_poster_url). editPano()
  // seeds panoDraft.yaw/pitch from these so the chooser re-opens at the last
  // chosen view; absent when never chosen (chooser opens forward-facing, 0/0).
  pano_initial_yaw?: number;
  pano_initial_pitch?: number;
  // media-04: "too_large" when the source exceeds the server's decode budget
  // (no preview exists — `url` is the multi-MB raw original); absent/"" when
  // processed fine. Server-set (CMSImage.ProcessingError) and patched live by
  // _handleImageProcessed. width/height ride along only with an error (the
  // tile's size label). The tile renders a placeholder + badge, never the raw.
  processing_error?: string;
  width?: number;
  height?: number;
}

// PROCESSING_ERROR_TOO_LARGE mirrors domain.PropertyImageProcessingErrorTooLarge.
const PROCESSING_ERROR_TOO_LARGE = "too_large";

// PoolImage entries never carry an org slug (out of the picker's scope), so
// the property-scoped media-kind PATCH route
// ("/organizations/{org}/properties/{slug}/images/{id}/media-kind") is built
// from two parts resolved separately: the org slug parsed out of this.baseURL
// ("/organizations/{orgSlug}/websites/{websiteID}" — a documented, stable
// literal shape, see CMSEditorWidgetCtx.BaseURL), and the property slug
// resolved by _mediaKindSlug (the pool entry's OWN property_slug, else the
// picker's this.slug — a site-wide/area entry has no property of its own and is
// PATCHed through the current property's route, which the server accepts for
// same-org rows with no property).

interface CMSImagePickerConfig {
  field: string;
  // Autosave routing key (which content file + overlay shape). Threaded through
  // the cms-image-changed event detail so the controller routes the write. See
  // service.CMSTarget* / editor.ts `_flush`.
  target: string;
  multi: boolean;
  slug: string;
  selected: string[];
  pool: PoolImage[];
  min: number;
  max: number;
  // Editor base URL (`/organizations/{orgSlug}/websites/{websiteID}`) — used to
  // reach the draft image upload endpoints via the `pool_image` uploader context.
  // Same base editor.ts autosave uses. Seeded by the widget template (E2).
  baseURL: string;
  // siteScoped marks a SITE-WIDE image field (Scope == CMSScopeSite, e.g. the
  // host portrait). When true the picker (a) shows the site-wide pool rows (empty
  // property_slug), (b) uploads WITHOUT a slug (server → AddUploadedSiteWide), and
  // (c) deletes WITHOUT ?slug= (server → RemoveSiteWide). Property fields (false)
  // keep the property-scoped behavior. Optional → defaults to false.
  siteScoped?: boolean;
  // fallbackURL is a display-only preview URL shown when nothing is selected (e.g.
  // the org operator photo on a pre-seed host portrait). Never a stored value.
  fallbackURL?: string;
  // areaSlug marks an AREA image field (area-guide page). When set, visiblePool
  // shows the union of the scopes selected in selectedScopes (default = this
  // area only). The per-entity scope dropdown supersedes browseAll for area
  // fields — browseAll is ignored entirely there (see visiblePool below).
  // Absent on property/site fields. Mutually exclusive with siteScoped in practice.
  areaSlug?: string;
  // defaultScope is the GENERAL entry point into scope-mode (the per-entity
  // dropdown + selectedScopes union model): a scope key ("property:<slug>" |
  // "area:<slug>" | "sitewide") naming this picker's own entity. When set, the
  // picker behaves exactly like an area picker does today, but for ANY entity
  // — not just areas. Optional → "". Backward-compat: existing area mounts
  // pass areaSlug (not defaultScope) and keep working unchanged via
  // _effectiveDefaultScope's fallback below. No current mount passes this yet.
  defaultScope?: string;
  // creditBaseUrl is the photo-credit PATCH base (Task 9), matching the legal
  // section's convention: "/organizations/{orgSlug}/websites/{websiteID}/api/legal"
  // (see cmsImageCreditsXData). Optional — when a mount doesn't seed it
  // explicitly, it's derived from baseURL below (same base every mount
  // already threads through for uploads), so no templ call site needs
  // updating just to light up the © affordance.
  creditBaseUrl?: string;
  // locale is the CMS's current edit locale (the `?locale=` the rest of the
  // editor already resolves — see variant-params.ts's buildVariantParams).
  // Drives roomName's single-authored-entry semantics (Q4): a room's name is
  // stored as a ONE-entry Record<locale, string> map, replaced wholesale on
  // every rename rather than merged across locales — a room authored in `de`
  // then renamed in `en` should not accumulate stale entries from earlier
  // locale sessions. Optional → defaults to "de" (the platform default
  // locale, see i18n resolution order in CLAUDE.md) so a mount/test that
  // omits it still behaves deterministically.
  locale?: string;
}

export function cmsImagePicker(config: CMSImagePickerConfig) {
  // The effective default scope key, computed once at construction from the
  // config (not `this` — the object literal below isn't assembled yet).
  // defaultScope wins when set; otherwise fall back to "area:"+areaSlug so
  // existing area mounts (which pass areaSlug, never defaultScope) are
  // byte-for-byte unaffected by this generalization.
  const eds = config.defaultScope || (config.areaSlug ? "area:" + config.areaSlug : "");
  return {
    field: config.field,
    target: config.target,
    multi: config.multi,
    min: config.min,
    max: config.max,
    slug: config.slug,
    baseURL: config.baseURL,
    // Falls back to the legal-section's own base ({baseURL}/api/legal, see
    // cmsImageCreditsXData) when the mount doesn't seed one explicitly —
    // every mount already carries a non-empty baseURL (the editor's own base
    // URL), so this resolves for every picker in the CMS editor without
    // threading a new config param through each of the 4 templ call sites.
    creditBaseUrl: config.creditBaseUrl || (config.baseURL ? config.baseURL + "/api/legal" : ""),
    // See CMSImagePickerConfig.locale's doc comment — drives roomName's
    // single-entry-map REPLACE semantics.
    locale: config.locale || "de",
    siteScoped: config.siteScoped === true,
    fallbackURL: config.fallbackURL || "",
    areaSlug: config.areaSlug || "",
    defaultScope: config.defaultScope || "",
    // image-pool.json is flat across all properties. Keep the FULL list and
    // filter at render time (visiblePool) so the browse-all toggle can widen
    // the view without a server round-trip.
    _allPool: [...(config.pool || [])] as PoolImage[],
    // Default view = this property's images + site-wide (no property_slug).
    // Toggle to show every property's images. Ignored entirely for an AREA
    // picker (areaSlug set) — areaScopes supersedes it there; kept for
    // property/site pickers, which still render the single checkbox.
    browseAll: false,
    // AREA-picker-only per-entity scope selection. Replaces the single browseAll
    // toggle for area context: the operator widens from "this area's images" to
    // additionally include specific linked accommodations and/or the site-wide
    // pool, each toggled independently. The set holds scope KEYS
    // ("area:<slug>" | "property:<slug>" | "sitewide"); the available options
    // (scopeOptions) and their labels are derived entirely from _allPool.
    // DEFAULT = this entity only (the effective default scope — see `eds`
    // above). Unused/irrelevant when not in scope mode (see _scopeMode).
    selectedScopes: eds ? [eds] : ([] as string[]),
    committed: [...config.selected] as string[], // strip + emitted value
    draft: [] as string[], // modal working copy
    q: "",
    // editingCreditFor is the id of the pool image currently in the right-pane
    // credit-edit takeover (Task 9), null when the normal pool grid shows.
    // creditDraft is the reactive form state cmsCreditFields binds to —
    // shaped like image-credits.ts's CreditImage, seeded from the matching
    // visiblePool entry's hasCredit/author/license/source_url/title fields
    // (see editCredit below). _hasLicense is UI-only (progressive disclosure).
    editingCreditFor: null as string | null,
    creditDraft: { id: "", url: "", author: "", license: "", source_url: "", title: "", _hasLicense: false } as CreditImage,
    // editingPanoFor is the id of the pool image currently in the right-pane
    // media-kind override takeover (Task 9), null when the normal pool grid
    // shows. panoDraft is the reactive state cmsPanoFields binds to, seeded
    // from the matching visiblePool entry (see editPano below). Mirrors
    // editingCreditFor/creditDraft exactly — the pool-grid gate (templ) hides
    // the grid whenever EITHER takeover is open.
    editingPanoFor: null as string | null,
    panoDraft: { id: "", url: "", poster_url: "", media_kind: "photo" } as PanoDraft,
    // cropOpen/cropReady drive the frame-in-viewer crop chooser section of
    // cmsPanoFields (below panoDraft's media-kind select, only shown when
    // panoDraft.media_kind === "pano"). cropOpen toggles the viewer host's
    // visibility (the "choose thumbnail" button vs. the mounted viewer);
    // cropReady gates the "use this view" button — disabled until
    // openPanoChooser()'s mount resolves, so the operator can't read a pose
    // from a viewer that isn't live yet. Both reset false whenever the pano
    // pane closes (see _teardownPanoCropChooser).
    cropOpen: false,
    cropReady: false,
    // cropSaveState drives the "use this view" lifecycle feedback: idle →
    // saving (PATCH in flight) → processing (poster re-extraction running,
    // awaiting the property_image_processed SSE) → done (poster refreshed) OR
    // error. The button is disabled while saving/processing (kills spam); the
    // inline status line in cmsPanoFields shows the phase. _cropTimer is the
    // safety timeout — re-extraction has no failure signal, so a lingering
    // "processing" self-clears rather than stranding the button forever.
    cropSaveState: "idle" as "idle" | "saving" | "processing" | "done" | "error",
    _cropTimer: null as ReturnType<typeof setTimeout> | null,
    // Third picker mode (Task B10): tour authoring (rooms + hotspot links,
    // one graph per property). Mutually exclusive with the credit/media-kind
    // takeovers — open()/openTour() each reset the others. NO `tourEnabled`
    // field here: whether the tour section is offered at all is a rail/
    // section-visibility concern, not picker state.
    tourMode: false,
    // Mobile-only (below lg) segmented toggle: which of the two stacked columns
    // is visible. "list" = the left context column (Selected / Rooms), "main" =
    // the browse grid or an active takeover editor. Inert on desktop (both
    // columns render side-by-side there). Defaults to "main" — browsing to add
    // is the primary action. Forced to "main" whenever a takeover opens so the
    // editor gets full height instead of sharing it with an irrelevant list.
    pickerMobilePane: "main" as "list" | "main",
    tourNodes: [] as TourNode[],
    // "" until a graph is loaded/seeded — _runTourSave() falls back to the
    // first node when still empty, mirroring openTour()'s own seed fallback.
    tourStartId: "",
    // Q4: replaces the old single-viewer editingTourNodeFor/tourDraft
    // takeover with a two-pane connections editor. null when the tour dialog
    // shows the room list; { open, fromId, toId } while a connection (new or
    // existing) is being placed. toId is "" until a target is chosen — see
    // openConnectionEditor/selectConnectionTarget.
    connectionEditor: null as { open: boolean; fromId: string; toId: string } | null,
    // Live viewer handles for the two panes (not part of connectionEditor's
    // reactive shape — mirrors _sortable's pattern of keeping non-serializable
    // handles as their own top-level field). null when no pane is mounted.
    _connPaneFrom: null as LinkPane | null,
    _connPaneTo: null as LinkPane | null,
    // The pose each pane was SEEDED with at mount time (existing saved link,
    // or the ~180°-behind-arrival guess for a brand-new "to" pane) — the
    // fallback closeConnectionEditor() uses when the operator never clicked
    // in a pane (getPose() still null; see the Q3 controller note on async
    // mounts).
    _connSeedFrom: null as { yaw: number; pitch: number } | null,
    _connSeedTo: null as { yaw: number; pitch: number } | null,
    // Pano-only pool gate for the room picker (a plain photo can't anchor a
    // 360° room) — flip on to browse the full visible pool when a wanted
    // image hasn't been classified as a pano yet.
    showAllInTour: false,
    // Autosave (Q4): serialized, debounced PUT of the whole graph. At most
    // ONE PUT in flight at a time — _tourSaving/_tourDirty coordinate a
    // mutation landing mid-save into exactly one follow-up, never an
    // overlapping call. tourSaveState drives the inline indicator (Q5).
    tourSaveState: "idle" as "idle" | "saving" | "saved" | "error",
    _tourSaveTimer: null as ReturnType<typeof setTimeout> | null,
    _tourSaving: false,
    _tourDirty: false,
    _onBeforeTourNav: null as (() => void) | null,
    uploading: false,
    _pendingUploads: 0,
    _sortable: null as Sortable | null,
    _onPoolUpload: null as ((e: Event) => void) | null,
    _onPoolUploadFailed: null as ((e: Event) => void) | null,
    _onImageProcessed: null as ((e: Event) => void) | null,
    // True between a save() emit and the editor's terminal ack for THIS target.
    // remove-from-pool is disabled while saving so the reference guard always
    // reads PERSISTED state (a just-deselected image must be committed before it
    // can be deleted as an unreferenced candidate). The gate releases on BOTH
    // outcomes: cms-autosave-flushed (PUT success) and cms-autosave-failed (PUT
    // failed → re-queued). Without the failure ack the gate would stick true
    // after a failed save, leaving remove-from-pool dead until reload.
    _saving: false,
    _onAutosaveFlushed: null as ((e: Event) => void) | null,
    _onAutosaveFailed: null as ((e: Event) => void) | null,

    init() {
      // Listen for fire-and-forget uploads completing (uploader can't await the
      // picker). Filtered to THIS picker's field+target in the handler. Bound ref
      // stored so destroy() can detach it on section swap / modal re-init.
      this._onPoolUpload = (e: Event) => this._handlePoolUpload((e as CustomEvent).detail);
      window.addEventListener("pool-image-uploaded", this._onPoolUpload);
      // Terminal upload failures (uploader exhausted retries / non-retryable):
      // no pool-image-uploaded will ever arrive, so we must decrement the pending
      // count here too — otherwise the spinner spins forever.
      this._onPoolUploadFailed = (e: Event) =>
        this._handlePoolUploadFailed((e as CustomEvent).detail);
      window.addEventListener("pool-image-upload-failed", this._onPoolUploadFailed);
      // Async process-job completion (SSE property_image_processed, layout.templ's
      // htmx:sseMessage bridge): patches media_kind/pano_poster_url onto the
      // matching pool entry INSTANTLY, no reload, so a just-uploaded pano's 360°
      // badge appears the moment the job finishes. Broadcast to every picker on
      // the page (like pool-image-uploaded above) — the id lookup is a no-op for
      // pickers whose pool doesn't contain it.
      this._onImageProcessed = (e: Event) =>
        this._handleImageProcessed((e as CustomEvent).detail);
      window.addEventListener("property_image_processed", this._onImageProcessed);
      // The editor (editor.ts._flush) acks a completed per-TARGET autosave. The
      // flush groups by target (not field), so we match on target ONLY to clear
      // the _saving gate that disables remove-from-pool while a save is pending.
      this._onAutosaveFlushed = (e: Event) => {
        const d = (e as CustomEvent).detail || {};
        if (d.target === this.target) this._saving = false;
      };
      document.body.addEventListener("cms-autosave-flushed", this._onAutosaveFlushed);
      // A failed PUT never acks via cms-autosave-flushed; editor.ts._flush emits
      // cms-autosave-failed per affected target on the catch path. Release the
      // gate on that terminal outcome too (matched on target, same as success)
      // so remove-from-pool recovers without a reload.
      this._onAutosaveFailed = (e: Event) => {
        const d = (e as CustomEvent).detail || {};
        if (d.target === this.target) this._saving = false;
      };
      document.body.addEventListener("cms-autosave-failed", this._onAutosaveFailed);
      // A boosted nav (htmx:before-request, fired body-wide for ANY htmx
      // request) or a hard reload (beforeunload) can both fire while a tour
      // autosave is still sitting in its debounce window — the pending
      // setTimeout doesn't survive a real navigation, and a swapped-out
      // Alpine component doesn't survive a boost. Flush synchronously before
      // either happens so a burst of edits right before nav isn't lost.
      this._onBeforeTourNav = () => this.flushTourSave();
      document.body.addEventListener("htmx:before-request", this._onBeforeTourNav);
      window.addEventListener("beforeunload", this._onBeforeTourNav);
    },
    destroy() {
      this._teardownSortable();
      this._teardownConnectionEditor();
      if (this._onPoolUpload) {
        window.removeEventListener("pool-image-uploaded", this._onPoolUpload);
        this._onPoolUpload = null;
      }
      if (this._onPoolUploadFailed) {
        window.removeEventListener("pool-image-upload-failed", this._onPoolUploadFailed);
        this._onPoolUploadFailed = null;
      }
      if (this._onImageProcessed) {
        window.removeEventListener("property_image_processed", this._onImageProcessed);
        this._onImageProcessed = null;
      }
      if (this._onAutosaveFlushed) {
        document.body.removeEventListener("cms-autosave-flushed", this._onAutosaveFlushed);
        this._onAutosaveFlushed = null;
      }
      if (this._onAutosaveFailed) {
        document.body.removeEventListener("cms-autosave-failed", this._onAutosaveFailed);
        this._onAutosaveFailed = null;
      }
      if (this._onBeforeTourNav) {
        document.body.removeEventListener("htmx:before-request", this._onBeforeTourNav);
        window.removeEventListener("beforeunload", this._onBeforeTourNav);
        this._onBeforeTourNav = null;
      }
      if (this._tourSaveTimer) {
        clearTimeout(this._tourSaveTimer);
        this._tourSaveTimer = null;
      }
    },

    // The effective default scope key for THIS picker: defaultScope when set,
    // else "area:"+areaSlug when areaSlug is set, else "" (not in scope mode).
    // Mirrors the `eds` constant computed at construction time (used only for
    // the initial selectedScopes seed, before `this` exists) — kept as its own
    // method here so scopeOptions/_scopeMode can call it live.
    _effectiveDefaultScope(): string {
      return this.defaultScope || (this.areaSlug ? "area:" + this.areaSlug : "");
    },

    // True when this picker is in per-entity scope-dropdown mode (an AREA
    // picker today, or any picker given defaultScope). False → the legacy
    // browseAll/siteScoped/property-default checkbox model applies unchanged.
    get _scopeMode(): boolean {
      return !!this._effectiveDefaultScope();
    },

    // Templ-facing alias — the dialog can't call a method starting with `_`
    // cleanly from x-show, so expose the same value under a plain name.
    get inScopeMode(): boolean {
      return this._scopeMode;
    },

    // The scope KEY of a pool entry: an area row → "area:<area_slug>", a
    // property row → "property:<property_slug>", otherwise the site-wide bucket.
    // Area and property are mutually exclusive (the server stamps only one). This
    // is the single mapping both scopeOptions and visiblePool agree on.
    _scopeKeyOf(p: PoolImage): string {
      if (p.area_slug) return "area:" + p.area_slug;
      if (p.property_slug) return "property:" + p.property_slug;
      return "sitewide";
    },

    // The per-entity dropdown options for a scope-mode picker (every picker
    // today — every server-side mount seeds a non-empty defaultScope, see
    // cmsImagePickerDefaultScope), DERIVED from _allPool: one option for THIS
    // entity (ALWAYS first — it's the default selection, so it must stay
    // visible/toggleable even with zero images yet), one per distinct linked
    // accommodation (first-seen order, skipping the current entity if it's
    // itself an accommodation — no duplicate), one per distinct AREA
    // (first-seen order, same skip rule — this closes the gap where an area's
    // images were invisible from every non-area picker's dropdown), the
    // site-wide bucket (if the pool has any and it isn't already the current
    // entity), and finally an "all images" option (parity with the legacy
    // browseAll checkbox below — see visiblePool). Empty for a non-scope-mode
    // picker (kept for the handful of direct TS-constructed pickers in tests
    // that omit both areaSlug and defaultScope).
    //
    // LABELS: entity/accommodation/area labels come from each entry's
    // scope_name (data). The site-wide and "all" options — and a current
    // entity with no rows to source a name from — get label "" ON PURPOSE:
    // the shared dialog templ (which owns `t`) supplies the localized
    // fallback text when label is empty. This keeps ALL i18n in the templ, so
    // no mount/handler has to thread a localized string through. Other
    // entities' rows never become options — only the effective default
    // scope's does.
    get scopeOptions(): Array<{ key: string; label: string }> {
      if (!this._scopeMode) return [];
      const eds = this._effectiveDefaultScope();
      const opts: Array<{ key: string; label: string }> = [];
      const seen = new Set<string>();
      // Current entity first, unconditionally. Label from any of its rows'
      // scope_name; "" when it has none yet (dialog renders a localized fallback).
      const currentRow = this._allPool.find((p) => this._scopeKeyOf(p) === eds);
      opts.push({ key: eds, label: currentRow?.scope_name || "" });
      seen.add(eds);
      // Accommodations next, in first-seen order (skip the current entity —
      // already pushed above, avoids a duplicate when eds is itself a property).
      for (const p of this._allPool) {
        if (!p.property_slug) continue;
        const key = "property:" + p.property_slug;
        if (seen.has(key)) continue;
        seen.add(key);
        opts.push({ key, label: p.scope_name || p.property_slug });
      }
      // Areas next, same first-seen/skip-current rule. Previously this loop
      // didn't exist at all, so an area's images were reachable ONLY from that
      // area's own picker — every property/site/homepage field's dropdown
      // silently dropped them even though the unified pool already carried
      // them (scope-stamped area:<slug>).
      for (const p of this._allPool) {
        if (!p.area_slug) continue;
        const key = "area:" + p.area_slug;
        if (seen.has(key)) continue;
        seen.add(key);
        opts.push({ key, label: p.scope_name || p.area_slug });
      }
      // Site-wide next, if the pool has any and it isn't already the current
      // entity. Label "" — the dialog localizes it.
      if (!seen.has("sitewide") && this._allPool.some((p) => !p.property_slug && !p.area_slug)) {
        opts.push({ key: "sitewide", label: "" });
      }
      // "All images" last — every picker is now scope-mode (defaultScope is
      // always non-empty server-side), which made the legacy browseAll
      // checkbox unreachable in practice. Without this, an operator could no
      // longer widen to literally everything the way browseAll used to;
      // "all" restores that breadth as an explicit scope-dropdown entry.
      // visiblePool special-cases this key to return _allPool unfiltered.
      opts.push({ key: "all", label: "" });
      return opts;
    },

    // Toggle a scope key in/out of the selection (checkbox behavior).
    toggleScope(key: string) {
      this.selectedScopes = this.selectedScopes.includes(key)
        ? this.selectedScopes.filter((k) => k !== key)
        : [...this.selectedScopes, key];
    },

    // Property-scoped (default), area-scoped, site-scoped, or full pool, depending
    // on the browse-all toggle (or, in scope mode, the per-entity selection).
    // A SITE-SCOPED field (host portrait) shows ONLY the site-wide rows (empty
    // property_slug) — a per-property image is never a valid host portrait. A
    // scope-mode picker (AREA field today, or any picker given defaultScope)
    // shows the UNION of the scopes selected in selectedScopes — a pool entry is
    // visible iff its scope key is selected. browseAll is ignored entirely in
    // scope mode (selectedScopes supersedes it; the dialog never renders that
    // checkbox there — see the templ). The property default branch MUST match
    // the old construction-time filter exactly (minus the leak fix below).
    get visiblePool(): PoolImage[] {
      if (this._scopeMode) {
        // "all" (see scopeOptions) bypasses the per-scope union entirely —
        // the full, unfiltered pool, matching the legacy browseAll checkbox.
        if (this.selectedScopes.includes("all")) return this._allPool;
        return this._allPool.filter((p) => this.selectedScopes.includes(this._scopeKeyOf(p)));
      }
      if (this.browseAll) return this._allPool;
      if (this.siteScoped) {
        return this._allPool.filter((p) => !p.property_slug && !p.area_slug);
      }
      // Property default: this property's own images + genuine site-wide rows.
      // `!p.area_slug` is the leak fix — an area row (empty property_slug,
      // non-empty area_slug) previously passed the `!p.property_slug` test and
      // leaked into every property picker's site-wide fallback.
      return this._allPool.filter(
        (p) => (!p.property_slug && !p.area_slug) || p.property_slug === this.slug,
      );
    },

    upload(files: FileList | File[]) {
      const list = Array.from(files || []);
      if (list.length === 0) return;
      const store = (window as any).Alpine?.store("uploader");
      if (!store) return;
      this._pendingUploads += list.length;
      this.uploading = true;
      // A SITE-SCOPED field uploads WITHOUT a slug so the confirm endpoint routes
      // to the site-level branch (AddUploadedSiteWide). Property fields send their
      // slug (property-scoped AddUploaded). The uploader omits the ?slug= query
      // param entirely for an empty slug (see getEndpoints in uploader.ts). An
      // AREA field must not also send ?slug= — area wins server-side anyway (see
      // getEndpoints' pool_image branch), but keep the request clean.
      const uploadSlug = this.siteScoped || this.areaSlug ? "" : this.slug;
      // Fire-and-forget: the uploader notifies us via the pool-image-uploaded
      // window event (see _handlePoolUpload) — do NOT await a result here.
      store.addFiles(list, {
        type: "pool_image",
        baseURL: this.baseURL,
        slug: uploadSlug,
        area: this.areaSlug || "",
        field: this.field,
        target: this.target,
      });
    },

    _handlePoolUpload(detail: {
      field: string;
      target: string;
      id: string;
      url: string;
      slug?: string;
      area?: string;
    }) {
      // Add the new image to THIS picker's pool — for EVERY picker on the page, not
      // just the uploader's. Sibling fields (e.g. favicon when you uploaded from
      // logo) otherwise wouldn't see the image until a reload re-seeds the pool.
      // Tag it with the upload's own scope: detail.area for an area upload,
      // otherwise detail.slug ("" = site-wide) for a property/site upload — the
      // two are mutually exclusive, matching upload()'s ?area=/?slug= choice. Each
      // picker's visiblePool filter then decides whether to show it. Dedupe by id
      // so the broadcast can't double-add.
      const uploadedImg: PoolImage = {
        id: detail.id,
        url: detail.url,
        property_slug: detail.area ? "" : detail.slug || "",
        area_slug: detail.area || "",
        search: "",
      } as PoolImage;
      if (!this._allPool.some((p) => p.id === detail.id)) {
        this._allPool.push(uploadedImg);
      }
      // Only the picker that INITIATED the upload auto-selects it and owns the
      // pending-upload spinner — a sibling neither selects nor tracks the count.
      if (detail.field !== this.field || detail.target !== this.target) return;
      // Keep the freshly-uploaded image visible in THIS picker's grid even when
      // its browse-scope default differs from its upload-scope (e.g.
      // location_bg_image_id defaults browse to the property's linked AREA
      // ["area:<slug>"], but an upload from it always tags "property:<slug>" —
      // uploads stay property-specific by design). Without this, the image is
      // selected/saved but invisible until the operator manually toggles the
      // matching scope on. "all" already shows everything, so skip in that case.
      if (this._scopeMode && !this.selectedScopes.includes("all")) {
        const key = this._scopeKeyOf(uploadedImg);
        if (!this.selectedScopes.includes(key)) {
          this.selectedScopes = [...this.selectedScopes, key];
        }
      }
      // Auto-select it: multi appends (respecting max), single replaces.
      if (this.multi) {
        if (!this.max || this.draft.length < this.max) this.draft = [...this.draft, detail.id];
      } else {
        this.draft = [detail.id];
      }
      this._pendingUploads = Math.max(0, this._pendingUploads - 1);
      if (this._pendingUploads === 0) this.uploading = false;
    },

    _handlePoolUploadFailed(detail: { field: string; target: string }) {
      // Ignore failures destined for a different picker on the page.
      if (detail.field !== this.field || detail.target !== this.target) return;
      // Mirror the success-counter bookkeeping, but add NOTHING to the pool — the
      // upload failed, so there is no new image. Success and failure are mutually
      // exclusive per task, so there's no risk of double-decrementing.
      this._pendingUploads = Math.max(0, this._pendingUploads - 1);
      if (this._pendingUploads === 0) this.uploading = false;
    },

    // _handleImageProcessed applies the async process job's completion (SSE
    // property_image_processed, raw JSON string in e.detail.data — see
    // layout.templ's htmx:sseMessage bridge) to the matching pool entry:
    // media_kind (photo|pano) + pano_poster_url, mirroring saveMediaKind's
    // map-update. Patches BOTH _allPool (this instance's working copy) and the
    // shared Alpine.store('cmsImagePool') (the ONE website-wide pool every
    // picker mount reads `pool:` from, cms_editor.templ) so the fix survives a
    // rail swap re-seeding a fresh picker from the store. A row not present in
    // either (e.g. a listing/site-wide image with no actor, or a picker whose
    // pool never included this id) is left untouched — safe no-op.
    _handleImageProcessed(detail: { data?: string }) {
      let d:
        | { image_id?: string; media_kind?: string; pano_poster_url?: string; url?: string; processing_error?: string }
        | undefined;
      try {
        d = JSON.parse(detail?.data || "");
      } catch {
        return;
      }
      if (!d || !d.image_id) return;
      const patch = (p: PoolImage): PoolImage =>
        p.id === d!.image_id
          ? {
              ...p,
              media_kind: d!.media_kind,
              pano_poster_url: d!.pano_poster_url,
              // Pano promotion replaces the raw upload with a viewer WebP and
              // DELETES the raw, so a fresh upload's url (the raw) is dead after
              // this event. Repoint when the event carries the new URL; empty =
              // storage unchanged, keep ours.
              ...(d!.url ? { url: d!.url } : {}),
              // media-04: "" = success → clears a stale marker; "too_large"
              // flips the tile to the placeholder + badge. Only touch the field
              // when the event carries the key, so an event without it can
              // never wipe an existing marker.
              ...("processing_error" in d!
                ? { processing_error: d!.processing_error || undefined }
                : {}),
            }
          : p;
      this._allPool = this._allPool.map(patch);
      const store = (window as any).Alpine?.store("cmsImagePool");
      if (Array.isArray(store)) {
        const idx = store.findIndex((p: PoolImage) => p.id === d!.image_id);
        if (idx !== -1) store[idx] = patch(store[idx]);
      }
      // If this completion is for the image whose crop chooser is open, refresh
      // the in-pane poster preview (the presigned URL changes per request, so
      // re-pointing the <img> busts the browser cache and the new crop appears)
      // and flip the "use this view" lifecycle to done.
      if (d.image_id === this.editingPanoFor) {
        if (d.pano_poster_url) this.panoDraft.poster_url = d.pano_poster_url;
        if (d.url) this.panoDraft.url = d.url;
        if ("processing_error" in d) {
          this.panoDraft.processing_error = d.processing_error || undefined;
        }
        // media-04: the poster job reverts a too-large manual pano flag to
        // photo. Sync the open pane so its select, crop section and openCrop
        // guard never act on a pano the server has already un-flagged.
        if (d.media_kind === "photo" && this.panoDraft.media_kind === "pano") {
          this.panoDraft.media_kind = "photo";
          this._teardownPanoCropChooser();
          if (this.cropSaveState === "processing") this._setCropState("idle");
        }
        if (this.cropSaveState === "processing") this._setCropState("done");
      }
    },

    open() {
      // Transactional: seed the working copy from the committed selection.
      this.draft = [...this.committed];
      // Always open on the pool grid. The credit/media-kind takeover panes are
      // only closed by their own "← Back" control; dismissing the modal via
      // ESC / backdrop / Cancel bypasses it, so a leftover editingCreditFor /
      // editingPanoFor would otherwise re-show that stale pane on the next open
      // instead of the grid. Reset both here so every open is deterministic.
      this.editingCreditFor = null;
      this.editingPanoFor = null;
      this.tourMode = false;
      this._teardownPanoCropChooser();
      this._teardownConnectionEditor();
      this.connectionEditor = null;
      ((this as any).$refs.dlg as HTMLDialogElement).showModal();
      (this as any).$nextTick(() => this.initSortable());
    },
    _teardownSortable() {
      if (this._sortable) {
        this._sortable.destroy();
        this._sortable = null;
      }
    },
    close() {
      // Cancel/✕/backdrop: discard the multi-select image draft (committed +
      // strip + preview unchanged). The tour CONNECTION editor is different —
      // autosave-first has no discard semantics, so an open connection editor
      // (both arrows placed, or an existing link being edited) must be
      // COMMITTED on dismissal, not thrown away. closeConnectionEditor()
      // reads each pane's live pose and calls saveConnection when a target
      // was chosen; it's a safe no-op teardown when connectionEditor is null
      // or no target was picked yet (add-flow, nothing to save).
      this._teardownSortable();
      this.closeConnectionEditor();
      // Tour mode has no explicit Save button (Q5: "Fertig" IS close()) — a
      // pending debounced autosave (including the connection just committed
      // above) must be flushed synchronously here so a burst of edits right
      // before dismissal isn't silently dropped.
      this.flushTourSave();
      ((this as any).$refs.dlg as HTMLDialogElement).close();
    },
    initSortable() {
      // Tour mode (B11): the room list (tourNodes) reorders through the SAME
      // createReorderable/applySortableReorder pair as the normal selected-list
      // below — applySortableReorder is generic over the array element type, it
      // only needs the DOM rows' data-id ATTRIBUTE to exist (never reads its
      // value), so reusing it for TourNode[] is a drop-in, not a new mechanism.
      // Branching here (rather than a second dedicated method) keeps the single
      // _sortable slot / _teardownSortable() destroy-first guard correct for
      // whichever list is actually visible — the two lists are never both
      // mounted-and-live at once (tourMode toggles which pane x-show shows).
      if (this.tourMode) {
        const el = (this as any).$refs.tourRoomList as HTMLElement | undefined;
        if (!el) return;
        this._teardownSortable();
        this._sortable = createReorderable(el, {
          handle: "[data-sort-handle]",
          draggable: "[data-id]",
          onEnd: (e: Sortable.SortableEvent) => {
            this.tourNodes = applySortableReorder(el, e, this.tourNodes, "data-id");
            this._scheduleTourSave();
          },
        });
        return;
      }
      const el = (this as any).$refs.selectedList as HTMLElement | undefined;
      if (!el || !this.multi) return;
      // Destroy-first: a native ESC/backdrop close fires the dialog's `close`
      // event without routing through close(); the @close handler tears down,
      // and this guard is belt-and-suspenders so a reopen never double-binds
      // the stable <ul> (which would double-fire onEnd → corrupt the reorder).
      this._teardownSortable();
      this._sortable = createReorderable(el, {
        handle: "[data-sort-handle]",
        draggable: "[data-id]",
        onEnd: (e: Sortable.SortableEvent) => {
          this.draft = applySortableReorder(el, e, this.draft, "data-id");
        },
      });
    },
    isInDraft(id: string): boolean {
      return this.draft.includes(id);
    },
    orderOf(id: string): number {
      return this.draft.indexOf(id) + 1;
    },
    toggle(id: string) {
      if (this.isInDraft(id)) {
        this.draft = this.draft.filter((x) => x !== id);
        return;
      }
      if (!this.multi) {
        this.draft = [id];
        return;
      }
      if (this.draft.length >= this.max) return; // gate max
      this.draft = [...this.draft, id];
    },
    removeFromDraft(id: string) {
      this.draft = this.draft.filter((x) => x !== id);
    },
    // isTooLarge reports whether the pool entry for id is stamped
    // processing_error="too_large" (media-04) — the selected strip swaps its
    // thumbnail for a placeholder. Resolves against the FULL pool like urlFor.
    isTooLarge(id: string): boolean {
      const m = this._allPool.find((p) => p.id === id);
      return !!m && m.processing_error === PROCESSING_ERROR_TOO_LARGE;
    },
    urlFor(id: string): string {
      // Resolve against the FULL pool: a selected id may be a just-uploaded or
      // (under browse-all) cross-property image not in the default-filtered view.
      const m = this._allPool.find((p) => p.id === id);
      // media-04: a too-large entry's url is the multi-MB raw original (no
      // preview could be generated) — never hand it to a thumbnail.
      if (m && m.processing_error === PROCESSING_ERROR_TOO_LARGE) return "";
      const u = m ? m.url || "" : "";
      // Every urlFor consumer renders a small (<=56px) thumbnail strip, but the
      // pool url is the 1920px WebP preview (0.3-1.3MB). Ask the /media proxy for
      // a small rendition (it resizes + caches on the fly; 480 is an allowlisted
      // width, conservative for a 56px slot at 2x DPR). Only rewrite our own
      // signed /media URLs; external/CDN fallbacks are left untouched.
      // Require BOTH /media/ and sig= so only our signed first-party proxy URLs
      // (which always carry ?exp=&sig=) are rewritten — a bare /media/ test would
      // corrupt an external/CDN url containing "/media/" but no query string
      // (…&w=480 appended to the path → 404).
      return u.includes("/media/") && u.includes("sig=") ? u + "&w=480" : u;
    },
    filteredPool(): PoolImage[] {
      const base = this.visiblePool;
      const q = this.q.trim().toLowerCase();
      if (!q) return base;
      return base.filter((p) => this._matchesSearch(p, q));
    },
    // _matchesSearch is the shared text-match predicate behind both
    // filteredPool() and tourPoolFiltered() (Q6) — the top search box must
    // narrow the tour room-picker grid the same way it narrows the normal
    // pool grid. Matches the precomputed server-built blob (caption + alt +
    // scene metadata + EN/DE enum synonyms). Falls back to per-field matching
    // for legacy raw-pool entries that lack the search field (no blob on
    // older sessions). `q` must already be trimmed/lowercased by the caller.
    _matchesSearch(p: PoolImage, q: string): boolean {
      if (p.search !== undefined) {
        return p.search.includes(q);
      }
      // Legacy fallback: raw pool entry without a precomputed blob.
      return [p.label, p.scene_description, p.scene_type, p.primary_function].some((s) =>
        (s || "").toLowerCase().includes(q),
      );
    },
    isValid(): boolean {
      return this.draft.length >= this.min && this.draft.length <= this.max;
    },
    countText(): string {
      return this.draft.length + " / " + (this.max || "∞"); // numeric, locale-free
    },
    save() {
      if (!this.isValid()) return;
      this.committed = [...this.draft];
      // Single image stores a scalar id (image_id); multi stores an array.
      const value: string | string[] = this.multi ? this.committed : this.committed[0] || "";
      ((this as any).$el as HTMLElement).dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: { field: this.field, target: this.target, value },
          bubbles: true,
        }),
      );
      // Gate remove-from-pool until the editor acks the autosave for this target
      // (cms-autosave-flushed). A just-deselected image must be committed before
      // it's deletable as an unreferenced candidate.
      this._saving = true;
      this.close();
    },

    // Only un-selected candidates may be removed from the pool (selected ones
    // stay selected — deselect first). Disabled while saving so the guard reads
    // persisted state. Drives the tile's remove affordance visibility. Site-wide
    // entries ARE removable now (a site-scoped field deletes WITHOUT a slug — the
    // server routes to the site-level RemoveSiteWide branch).
    canRemoveFromPool(id: string): boolean {
      return !this.isInDraft(id) && !this.committed.includes(id) && !this._saving;
    },

    // removeFromPool is the guarded "remove from pool" action (draft image delete
    // endpoint). The server soft-archives the image when it's still referenced by
    // other live/draft content and hard-deletes it otherwise — either way it
    // returns 204 with an HX-Trigger `flash-message` toast describing what
    // happened ("removed" vs "removed — still shown on X until you remove it
    // there"). Because this is a raw fetch (not HTMX), that header is NOT processed
    // automatically — we MUST call processResponseTriggers(res) to surface the
    // toast, or the operator sees nothing. 409 is now only a defensive fallback
    // (the soft-archive path no longer blocks); we still surface its message if it
    // ever occurs.
    async removeFromPool(id: string) {
      if (!this.canRemoveFromPool(id)) return;
      const root = (this as any).$root as HTMLElement;
      const confirmMsg = root?.dataset?.removeConfirm || "";
      if (confirmMsg && !(await studioConfirm(confirmMsg))) return;
      // The themed confirm is async: re-check the guard against the state as
      // it is now (a save may have started or the image been selected meanwhile).
      if (!this.canRemoveFromPool(id)) return;
      // Slug selects the delete branch on the server:
      //   - SITE-SCOPED field (host portrait) → NO slug → site-level RemoveSiteWide
      //     (org-scoped delete of the site-wide image).
      //   - property field → a non-empty slug resolves the property and removes by
      //     (propertyID, imageID) under an ownership check (mismatch 404s). Prefer
      //     the pool entry's owning property over this.slug so the real owner is
      //     used. The slug picks WHICH property, not a mere hint.
      let slug = "";
      if (!this.siteScoped) {
        const owner = this._allPool.find((p) => p.id === id);
        slug = owner?.property_slug || this.slug || "";
      }
      // Omit the ?slug= query param entirely when empty so the server sees no slug
      // at all and takes the site-level branch.
      const url = slug
        ? `${this.baseURL}/api/draft/images/${encodeURIComponent(id)}?slug=${encodeURIComponent(slug)}`
        : `${this.baseURL}/api/draft/images/${encodeURIComponent(id)}`;
      try {
        const res = await fetch(url, {
          method: "DELETE",
          credentials: "same-origin",
          headers: { "X-CSRF-Token": getCSRFToken() },
        });
        if (res.status === 409) {
          const body = (await res.json().catch(() => ({}))) as {
            message?: string;
            references?: Array<{ website?: string; section?: string; url?: string }>;
          };
          const ref = body.references && body.references[0];
          let msg = body.message || root?.dataset?.removeBlocked || "";
          if (ref && (ref.website || ref.section)) {
            msg = `${msg} (${[ref.website, ref.section].filter(Boolean).join(" · ")})`;
          }
          dispatchToast(msg, "error");
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // Surface the server's HX-Trigger flash-message (success/archived toast).
        // A raw fetch ignores HX-Trigger, so without this the operator gets no
        // feedback that the image was removed/archived.
        processResponseTriggers(res);
        // Drop the deleted candidate from the pool view.
        this._allPool = this._allPool.filter((p) => p.id !== id);
      } catch (err) {
        console.error("[image-picker] removeFromPool failed", err);
        // Empty-string fallback (renders nothing) rather than a hardcoded
        // English literal — data-remove-blocked is threaded (localized) on
        // every picker mount root.
        dispatchToast(root?.dataset?.removeBlocked || "", "error");
      }
    },

    // editCredit opens the right-pane credit-edit takeover (Task 9) for one
    // pool image, seeding creditDraft from its current credit fields (blank
    // when it has none yet — a fresh credit). Resolved against visiblePool
    // (not _allPool) so the picker only ever opens the takeover for an image
    // the operator can currently see. A no-op guard on creditBaseUrl: every
    // CMS-editor mount resolves one (see the config default above), so this
    // only protects a picker instance constructed without a baseURL at all
    // (defensive — no known caller does this today).
    editCredit(id: string) {
      if (!this.creditBaseUrl) return;
      const img = this.visiblePool.find((p) => p.id === id);
      if (!img) return;
      this.pickerMobilePane = "main"; // takeover needs full mobile height
      this.creditDraft = {
        id: img.id,
        url: img.url || "",
        author: img.author || "",
        license: img.license || "",
        source_url: img.source_url || "",
        title: img.title || "",
        _hasLicense: !!img.license,
        // media-04: the takeover renders a placeholder, never the raw url.
        processing_error: img.processing_error,
        width: img.width,
        height: img.height,
      };
      this.editingCreditFor = id;
    },

    // saveCredit persists creditDraft via the shared saveImageCredit() (image-
    // credits.ts) — the exact fetch + resp.ok gate + website-updated/
    // cms-external-dirty dual dispatch the legal-section credit list uses, so
    // this dedups with cmsImageCredits.save rather than re-implementing the
    // PATCH. On success: mirror the just-saved fields onto the pool entry
    // locally (so the tile updates immediately, without waiting for a
    // reload) — license/source_url are gated behind _hasLicense, EXACTLY
    // matching saveImageCredit's PATCH body, so this local echo never shows
    // a field the server didn't actually persist. hasCredit is derived from
    // whether ANY field survived the gate (not unconditionally true): a save
    // that clears every field (the all-empty ClearCredit path, see
    // handler_website_image_credits.go) must drop the © accent immediately
    // instead of showing a stale customized state until reload.
    //
    // cmsCreditFields fires this on EVERY field @change (per-field autosave,
    // same as the legal-section list). It must NOT close the takeover — doing
    // so would bounce the operator back to the grid the moment they tick the
    // "under a license" checkbox or edit any field. The takeover is closed
    // ONLY by the explicit "← Back" control (editingCreditFor = null); by then
    // every edit has already autosaved on blur, so Back needs no final save.
    async saveCredit() {
      if (!this.creditBaseUrl || !this.editingCreditFor) return;
      // Thread the localized save-failed toast so saveImageCredit doesn't fall
      // back to an English literal. The message lives on the shared dialog
      // (cms_image_picker_dialog.templ, data-credit-save-failed) — the only
      // surface that renders this credit-edit takeover — so it's read here
      // rather than plumbed through every picker mount's x-data config.
      const failedMsg =
        ((this as any).$refs?.dlg as HTMLElement | undefined)?.dataset?.creditSaveFailed || "";
      const ok = await saveImageCredit(this.creditBaseUrl, this.creditDraft, failedMsg);
      if (!ok) return;
      const id = this.editingCreditFor;
      const draft = this.creditDraft;
      const author = draft.author || "";
      const license = draft._hasLicense ? draft.license || "" : "";
      const sourceURL = draft._hasLicense ? draft.source_url || "" : "";
      const title = draft.title || "";
      const hasCredit = !!(author || license || sourceURL || title);
      this._allPool = this._allPool.map((p) =>
        p.id === id
          ? { ...p, hasCredit, author, license, source_url: sourceURL, title }
          : p,
      );
    },

    // editPano opens the right-pane media-kind override takeover (Task 9) for
    // one pool image, seeding panoDraft from its current fields. Resolved
    // against visiblePool (not _allPool) — mirrors editCredit — so the
    // picker only ever opens the takeover for an image the operator can
    // currently see.
    editPano(id: string) {
      const img = this.visiblePool.find((p) => p.id === id);
      if (!img) return;
      this.pickerMobilePane = "main"; // takeover needs full mobile height
      this.panoDraft = {
        id: img.id,
        url: this._panoSrc(img),
        poster_url: img.pano_poster_url || "",
        media_kind: img.media_kind || "photo",
        // Seed from the pool entry's stored viewpoint (DEGREES) so the chooser
        // re-opens at the last-chosen framing; undefined when never chosen, and
        // the chooser then opens forward-facing (0/0).
        yaw: img.pano_initial_yaw,
        pitch: img.pano_initial_pitch,
        // media-04: a too-large image shows the placeholder and never mounts
        // the 360° crop chooser (the flip-to-photo select still works).
        processing_error: img.processing_error,
        width: img.width,
        height: img.height,
      };
      this.editingPanoFor = id;
      this.cropOpen = false;
      this.cropReady = false;
    },

    // _orgSlug parses the org slug out of this.baseURL
    // ("/organizations/{orgSlug}/websites/{websiteID}", a stable literal
    // shape documented on CMSEditorWidgetCtx.BaseURL) — the media-kind route
    // is property-scoped, not website-scoped, so it can't reuse baseURL or
    // creditBaseUrl directly; every mount already carries baseURL, so this
    // avoids threading a new config field through the picker's 4 templ call
    // sites just for one org slug.
    _orgSlug(): string {
      return this.baseURL.split("/")[2] || "";
    },

    // _mediaKindSlug resolves the property slug for the media-kind / pano-crop
    // PATCH route. A property image uses ITS OWN property_slug (browse-all may
    // show another property's image — the server only accepts it on its own
    // property's route). A site-wide/area entry (empty property_slug) has no
    // property and goes through the current picker's this.slug: the server
    // accepts a no-property row of the route's org on any of its property
    // routes (PropertyImageService.imageEditableFromRoute).
    _mediaKindSlug(id: string): string {
      const owner = this._allPool.find((p) => p.id === id);
      return owner?.property_slug || this.slug || "";
    },

    // canEditPano gates the tile's 360° trigger (cmsImageTile canEditPanoExpr):
    // hidden only when no property slug resolves at all (a site-wide/area entry
    // in a picker with no property context) — there is no route to PATCH.
    canEditPano(img: PoolImage): boolean {
      return !!img && this._mediaKindSlug(img.id) !== "";
    },

    // saveMediaKind persists panoDraft.media_kind via the shared
    // savePanoOverride() (pano-override.ts) — the exact fetch + resp.ok gate
    // + website-updated/cms-external-dirty dual dispatch saveCredit above
    // uses. Property slug resolution: see _mediaKindSlug.
    // Must NOT close the takeover — same per-field-autosave contract as
    // saveCredit; the pane is closed ONLY by the explicit "← Back" control.
    async saveMediaKind(kind: string) {
      if (!this.editingPanoFor) return;
      const id = this.editingPanoFor;
      const owner = this._allPool.find((p) => p.id === id);
      const slug = this._mediaKindSlug(id);
      const org = this._orgSlug();
      // No resolvable property slug (a site-wide/area entry in a picker with
      // no property context) — no route to PATCH. Silent no-op (the 360°
      // trigger is hidden for these via canEditPano anyway).
      if (!org || !slug) return;
      const baseUrl = `/organizations/${encodeURIComponent(org)}/properties/${encodeURIComponent(slug)}/images`;
      const failedMsg =
        ((this as any).$refs?.dlg as HTMLElement | undefined)?.dataset?.panoSaveFailed || "";
      const ok = await savePanoOverride(baseUrl, id, kind, failedMsg);
      if (!ok) {
        // The select is x-model-bound, so panoDraft.media_kind already holds
        // the rejected value — revert it to the persisted kind so the select
        // (and the pano-only crop section it gates) reflect the server state.
        // Only when the pane is still on this image (the save is async).
        if (this.editingPanoFor === id) {
          this.panoDraft.media_kind = owner?.media_kind || "photo";
        }
        return;
      }
      this.panoDraft.media_kind = kind;
      this._allPool = this._allPool.map((p) => (p.id === id ? { ...p, media_kind: kind } : p));
    },

    // openCrop is the "choose thumbnail / default view" trigger — a toggle:
    // closed -> reveal the host, load the viewer bundle (on-demand, first
    // open only) and mount it framed at panoDraft's current yaw/pitch; open
    // -> tear it down and hide again (mirrors the credit takeover's own
    // "← Back" dismiss semantics, just scoped to this sub-section instead of
    // the whole pane). $nextTick before mounting lets the x-show flip to
    // "cropOpen" actually paint (real dimensions) before pano-crop.ts
    // measures the host; resizePanoChooser() after mount is belt-and-
    // suspenders for any layout reflow the viewer's own DOM insertion causes.
    async openCrop() {
      if (this.cropOpen) {
        this._teardownPanoCropChooser();
        return;
      }
      // media-04: never feed a too-large image's raw original to the viewer.
      if (this.panoDraft.processing_error === PROCESSING_ERROR_TOO_LARGE) return;
      const host = (this as any).$refs.panoCropHost as HTMLElement | undefined;
      if (!host) return;
      this.cropOpen = true;
      this.cropReady = false;
      await (this as any).$nextTick();
      const dlg = (this as any).$refs?.dlg as HTMLElement | undefined;
      try {
        await openPanoChooser(host, this.panoDraft.url, {
          yaw: this.panoDraft.yaw,
          pitch: this.panoDraft.pitch,
        });
        resizePanoChooser();
        this.cropReady = true;
      } catch (err) {
        console.error("cms pano crop chooser failed to load", err);
        dispatchToast(dlg?.dataset?.panoCropLoadFailed || "", "error");
        this.cropOpen = false;
        // If mount succeeded but a later step (resize) threw, a handle is live —
        // tear it down so a failed open never leaks a WebGL context.
        this._teardownPanoCropChooser();
      }
    },

    // useThisView reads the live viewer's current framing and persists it via
    // savePanoCrop() (pano-override.ts). Property slug resolution mirrors
    // saveMediaKind exactly (owning pool entry's property_slug, falling back
    // to this.slug). Does NOT close the takeover or the crop section — the
    // operator may keep adjusting and re-save, same per-field-autosave
    // contract as saveMediaKind/saveCredit. Toasts "updating…" rather than
    // relying on the server's NoContent toast (which the raw fetch never
    // surfaces anyway, see savePanoCrop's doc comment) — the poster
    // re-extraction is async, so "saved" would be premature; the tile
    // refreshes itself once the property_image_processed SSE lands
    // (_handleImageProcessed above).
    async useThisView() {
      // Guard re-entry: a click while a save is already saving/processing is a
      // no-op (the button is also disabled in those states — belt + braces).
      if (this.cropSaveState === "saving" || this.cropSaveState === "processing") return;
      const pose = getChosenPose();
      if (!pose || !this.editingPanoFor) return;
      const id = this.editingPanoFor;
      const slug = this._mediaKindSlug(id);
      const org = this._orgSlug();
      if (!org || !slug) return;
      const baseUrl = `/organizations/${encodeURIComponent(org)}/properties/${encodeURIComponent(slug)}/images`;
      const dlg = (this as any).$refs?.dlg as HTMLElement | undefined;
      this._setCropState("saving");
      const ok = await savePanoCrop(baseUrl, id, pose, dlg?.dataset?.panoCropSaveFailed || "");
      if (!ok) {
        this._setCropState("error");
        return;
      }
      this.panoDraft.yaw = pose.yaw;
      this.panoDraft.pitch = pose.pitch;
      // Mirror onto the pool so editPano() re-seeds the just-saved angle on a
      // Back → reopen within the same session (matches saveMediaKind).
      this._allPool = this._allPool.map((p) =>
        p.id === id ? { ...p, pano_initial_yaw: pose.yaw, pano_initial_pitch: pose.pitch } : p,
      );
      // The 204 only means "queued" — the poster isn't ready yet. Enter
      // "processing" and wait for the re-extraction's SSE (_handleImageProcessed
      // flips us to "done" + refreshes the preview), or the safety timeout.
      this._setCropState("processing");
    },

    // _setCropState transitions the "use this view" lifecycle and manages the
    // single safety timer: a stuck "processing" (no SSE — the re-extraction has
    // no failure channel) self-clears after 20s so the button re-enables;
    // "done"/"error" auto-fade back to idle after a few seconds.
    _setCropState(s: "idle" | "saving" | "processing" | "done" | "error") {
      if (this._cropTimer) {
        clearTimeout(this._cropTimer);
        this._cropTimer = null;
      }
      this.cropSaveState = s;
      if (s === "processing") {
        this._cropTimer = setTimeout(() => {
          if (this.cropSaveState === "processing") this._setCropState("idle");
        }, 20000);
      } else if (s === "done" || s === "error") {
        this._cropTimer = setTimeout(() => {
          if (this.cropSaveState === s) this.cropSaveState = "idle";
        }, 3000);
      }
    },

    // _teardownPanoCropChooser releases the viewer's WebGL context and resets
    // the section's UI state. Called from every path that can dismiss the
    // pano pane or the crop section within it: the "← Back" button, the
    // dialog's native @close (ESC/backdrop bypasses Back), a fresh modal
    // open() (destroy-first, mirrors _teardownSortable), and openCrop()'s own
    // toggle-closed branch. Safe to call when nothing is mounted.
    _teardownPanoCropChooser() {
      teardownPanoChooser();
      this.cropOpen = false;
      this.cropReady = false;
      if (this._cropTimer) {
        clearTimeout(this._cropTimer);
        this._cropTimer = null;
      }
      this.cropSaveState = "idle";
    },

    // _tourBaseUrl is the property-scoped prefix tour-api.ts appends "/tour"
    // to. Unlike saveMediaKind/useThisView (which resolve the owning property
    // PER IMAGE, because browse-all can show a cross-property pool), the tour
    // graph is a single per-property document — the picker mount context
    // (this.slug) IS the property, so no per-node owner lookup is needed.
    _tourBaseUrl(): string {
      return `/organizations/${encodeURIComponent(this._orgSlug())}/properties/${encodeURIComponent(this.slug)}`;
    },

    // The display label for a room in the connections list / marker tooltip:
    // the room's own name (locale-aware — prefers the CURRENT edit locale
    // first, matching roomName's single-authored-entry semantics, then falls
    // back across de/en for a room authored in a different session/locale),
    // or its raw id when unnamed yet. Without the this.locale-first lookup, a
    // room authored in a non-de/en locale (e.g. `fr`) showed its raw id here
    // even though roomName() itself resolves it correctly for that locale.
    _roomLabel(id: string): string {
      const n = this.tourNodes.find((x) => x.id === id);
      if (!n) return id;
      return n.name?.[this.locale] || n.name?.["de"] || n.name?.["en"] || id;
    },

    // The marker set a link pane renders for one room's existing outbound
    // links (Q4 replacement for the deleted _markersFor — same shape, new
    // name since the old one was tied to the single-viewer placer's mount
    // signature).
    // Panorama source for the 360° panes and crop chooser: prefer the optimized
    // (≤8192px) rendition. A pano's raw `url` is too large for mobile GPUs (PSV
    // then shows "This panorama cannot be loaded") and may be gone from R2 after
    // the viewer-equirect repoint; the optimized URL is exactly what the public
    // tour loads, which works on the same device.
    _panoSrc(img: PoolImage): string {
      return img.optimized_url || img.url || "";
    },

    _linkMarkers(id: string): PanoMarker[] {
      const n = this.tourNodes.find((x) => x.id === id);
      if (!n) return [];
      return n.links.map((l) => ({ id: l.target, yaw: l.yaw, pitch: l.pitch, label: this._roomLabel(l.target) }));
    },

    // openTour switches the picker into tour-authoring mode and loads the
    // property's saved graph (empty graph for a property with none yet — see
    // loadTourGraph's own doc comment). Resets the OTHER two takeovers (a
    // stray editingCreditFor/editingPanoFor from a previous session must
    // never bleed into tour mode) — mirrors open()'s own reset block.
    async openTour() {
      this.tourMode = true;
      this.editingCreditFor = null;
      this.editingPanoFor = null;
      this._teardownPanoCropChooser();
      this._teardownConnectionEditor();
      this.connectionEditor = null;
      const graph = await loadTourGraph(this._tourBaseUrl());
      this.tourNodes = graph.nodes ?? [];
      this.tourStartId = graph.start_node_id || this.tourNodes[0]?.id || "";
      // A fresh graph load must never carry over a stale "error"/"saved"
      // banner from a previous tour-editing session on this same picker
      // instance (Alpine components are re-entered without a full teardown).
      this.tourSaveState = "idle";
      this._tourDirty = false;
      ((this as any).$refs.dlg as HTMLDialogElement).showModal();
      // B11: wire the room list's drag-to-reorder the same way open() wires the
      // selected-image list — see initSortable()'s tourMode branch.
      (this as any).$nextTick(() => this.initSortable());
    },

    // addRoom promotes a pano pool image to a tour room (idempotent — adding
    // an already-present room is a no-op, e.g. a double-click on the tile
    // affordance). order = current length keeps rooms in add-order by
    // default; the operator can still drag-reorder the room list (B11).
    addRoom(id: string) {
      if (this.tourNodes.some((n: TourNode) => n.id === id)) return;
      this.tourNodes.push({ id, name: {}, order: this.tourNodes.length, links: [] });
      this._scheduleTourSave();
    },

    // removeRoom drops the room AND every inbound link pointing at it from
    // every other room (an orphaned link target would crash the visitor-side
    // hotspot renderer). Resets tourStartId to the next remaining room (or ""
    // if the tour is now empty) when the removed room was the start. Also
    // closes the connections editor if it was open on the removed room —
    // otherwise a stale connectionEditor.fromId/toId would point at a room
    // that no longer exists.
    removeRoom(id: string) {
      this.tourNodes = this.tourNodes
        .filter((n: TourNode) => n.id !== id)
        .map((n: TourNode) => ({ ...n, links: n.links.filter((l) => l.target !== id) }));
      if (this.tourStartId === id) this.tourStartId = this.tourNodes[0]?.id ?? "";
      if (
        this.connectionEditor &&
        (this.connectionEditor.fromId === id || this.connectionEditor.toId === id)
      ) {
        this._teardownConnectionEditor();
        this.connectionEditor = null;
      }
      this._scheduleTourSave();
    },

    // roomName is BOTH the getter and setter for a room's authored name,
    // keyed to a SINGLE locale (this.locale — the CMS's current edit
    // locale). Called with one arg to read (a templ `:value` binding), two
    // args to write (an `@input`/`@change` handler) — Alpine's x-model needs
    // an assignable expression, which a per-room, locale-aware accessor
    // isn't, so get/set are exposed as one overloaded method instead.
    //
    // The setter REPLACES the whole name map rather than merging a key in:
    // `n.name = { [this.locale]: value }` drops any OTHER locale's entry
    // (e.g. one authored in a prior `en` session before the operator
    // switched to `de`) so at most one authored entry ever survives —
    // avoiding the ambiguity of "which locale's name is canonical" the old
    // two-field de/en model had no answer for.
    roomName(id: string, value?: string): string {
      const n = this.tourNodes.find((x: TourNode) => x.id === id);
      if (!n) return "";
      if (value !== undefined) {
        n.name = { [this.locale]: value };
        this._scheduleTourSave();
        return value;
      }
      return n.name?.[this.locale] ?? "";
    },

    setStartRoom(id: string) {
      this.tourStartId = id;
      this._scheduleTourSave();
    },

    // _mountFromPane/_mountToPane mount one side of the two-pane connection
    // editor via mountLinkPane (Q3) — split in two so selectConnectionTarget
    // (choosing a target AFTER opening with only fromId) can mount just the
    // "to" side without re-mounting (and losing) the "from" side's
    // in-progress placement.
    // _connMarkersFor builds one pane's marker set: the room's OTHER
    // outbound links (unchanged from _linkMarkers) PLUS, when editing an
    // EXISTING connection, that connection's own current arrow — seeded
    // under PLACEMENT_MARKER_ID so it's the SAME marker slot the placement
    // click handler (pano-editor.ts) upserts into. Excluding the edited
    // target from the "other links" pass matters: without it, an edit-mode
    // room would show TWO dots for the same connection after the first
    // click — the OLD one (this room's own link entry, id=target, never
    // removed) and the NEW placement dot (id=__placement__) sitting right
    // next to it.
    _connMarkersFor(id: string, editedTargetId: string, existing?: { yaw: number; pitch: number }): PanoMarker[] {
      const others = this._linkMarkers(id).filter((m) => m.id !== editedTargetId);
      if (!existing) return others;
      return [...others, { id: PLACEMENT_MARKER_ID, yaw: existing.yaw, pitch: existing.pitch, label: this._roomLabel(editedTargetId) }];
    },

    _mountFromPane(fromId: string, toId: string) {
      const host = (this as any).$refs?.linkFromHost as HTMLElement | undefined;
      const img = this.visiblePool.find((p: PoolImage) => p.id === fromId) ?? this._allPool.find((p) => p.id === fromId);
      if (!host || !img) return;
      // media-04: never mount a 360° pane on a too-large raw original.
      if (img.processing_error === PROCESSING_ERROR_TOO_LARGE) return;
      const a = this.tourNodes.find((n: TourNode) => n.id === fromId);
      const existing = toId && a ? a.links.find((l) => l.target === toId) : undefined;
      const seed = existing ? { yaw: existing.yaw, pitch: existing.pitch } : { yaw: 0, pitch: 0 };
      this._connSeedFrom = seed;
      // Destroy any PREVIOUSLY mounted from-pane before overwriting the
      // handle (Q2): before selectConnectionFrom existed, this method was
      // only ever called once per open (always preceded by
      // openConnectionEditor's own _teardownConnectionEditor()), so
      // _connPaneFrom was always null here. Q2's whole point is repeated
      // in-place swaps via the always-visible dropdown WITHOUT a full
      // teardown — without this destroy, every swap after the first would
      // leak the previous pane's WebGL context.
      this._connPaneFrom?.destroy();
      this._connPaneFrom = mountLinkPane(host, this._panoSrc(img), {
        yaw: seed.yaw,
        pitch: seed.pitch,
        markers: this._connMarkersFor(fromId, toId, existing),
        // S4: every placement/adjustment autosaves immediately — see
        // _onConnectionPanePlaced's own doc comment.
        onPlace: () => this._onConnectionPanePlaced(),
      });
    },

    _mountToPane(fromId: string, toId: string) {
      const host = (this as any).$refs?.linkToHost as HTMLElement | undefined;
      const img = this.visiblePool.find((p: PoolImage) => p.id === toId) ?? this._allPool.find((p) => p.id === toId);
      if (!host || !img) return;
      // media-04: never mount a 360° pane on a too-large raw original.
      if (img.processing_error === PROCESSING_ERROR_TOO_LARGE) return;
      const b = this.tourNodes.find((n: TourNode) => n.id === toId);
      const existing = b ? b.links.find((l) => l.target === fromId) : undefined;
      // Pre-seed the "to" pane's initial orientation ~180° behind the "from"
      // pane's CURRENT pose — the same "roughly behind the arrival
      // direction" guess the old placeLink() used for its auto-reciprocal —
      // unless an existing back-link already has a saved pose (edit mode).
      // Prefer the LIVE from-pane pose (getPose()) over the seed it was
      // mounted with: in the deferred-target flow (openConnectionEditor
      // without toId → operator places the from-arrow → THEN
      // selectConnectionTarget), the from-pane may already have a
      // just-placed pose by the time this mounts — using the stale
      // _connSeedFrom would pre-seed the reciprocal guess 180° off from
      // what the operator actually pointed at. Falls back to _connSeedFrom
      // when nothing's been placed yet (getPose() null) — same as the
      // both-ids-known path, where the from-pane is untouched.
      const fromBase = this._connPaneFrom?.getPose() ?? this._connSeedFrom ?? { yaw: 0, pitch: 0 };
      const seed = existing
        ? { yaw: existing.yaw, pitch: existing.pitch }
        : { yaw: (fromBase.yaw + 180) % 360, pitch: fromBase.pitch };
      this._connSeedTo = seed;
      // Destroy any PREVIOUSLY mounted to-pane before overwriting the handle
      // (Q2) — same reasoning as _mountFromPane's own destroy-before-remount
      // above: selectConnectionTarget can now swap an ALREADY-set toId via
      // the always-visible dropdown, not just fill in an empty one, so
      // _connPaneTo is no longer guaranteed null here.
      this._connPaneTo?.destroy();
      this._connPaneTo = mountLinkPane(host, this._panoSrc(img), {
        yaw: seed.yaw,
        pitch: seed.pitch,
        markers: this._connMarkersFor(toId, fromId, existing),
        // S4: every placement/adjustment autosaves immediately — see
        // _onConnectionPanePlaced's own doc comment.
        onPlace: () => this._onConnectionPanePlaced(),
      });
    },

    // openConnectionEditor opens the two-pane hotspot-link editor for a
    // connection between `fromId` and `toId` (existing connection = edit
    // mode; toId omitted = adding a new one, "to" pane mounts once the
    // operator picks a target via selectConnectionTarget). Tears down any
    // ALREADY-open editor's two panes FIRST — per-pane cleanup, not a module
    // singleton (mountLinkPane itself never tears down a prior pane, see
    // Q3), so re-opening a different connection without this leak both old
    // WebGL viewers.
    openConnectionEditor(fromId: string, toId?: string) {
      this._teardownConnectionEditor();
      if (!this.tourNodes.some((n: TourNode) => n.id === fromId)) return;
      this.pickerMobilePane = "main"; // link editor (two 360 panes) needs full mobile height
      this.connectionEditor = { open: true, fromId, toId: toId || "" };
      this._mountFromPane(fromId, toId || "");
      if (toId) this._mountToPane(fromId, toId);
    },

    // selectConnectionTarget mounts the "to" pane once the operator picks a
    // target — originally only reachable from the add-connection target
    // picker (Q5, called with toId still ""), now ALSO reachable from the
    // always-visible "to room" dropdown (Q2) to swap an already-chosen
    // target in edit mode. openConnectionEditor itself only mounts the
    // "from" pane when called without toId.
    //
    // Flushes via _onConnectionPanePlaced() FIRST, before reassigning toId
    // — mirrors selectConnectionFrom exactly, and for the same reason: Q2
    // made the "to" pane repeatedly re-selectable without a full teardown,
    // so a from-arrow placed in the add-flow (toId still "" —
    // _onConnectionPanePlaced no-ops until a target exists, see its own
    // doc comment) sits UNSAVED in the live from-pane viewer the whole
    // time. Picking a target here used to leave it unsaved too (nothing
    // commits it); if the operator then picked ANOTHER target before ever
    // clicking in the to-pane, _mountToPane's destroy-and-remount (the Q2
    // leak fix above) would silently discard that still-uncommitted from
    // pose with it — no save, no toast. The flush is a no-op once
    // toId is already set to the SAME pair nothing changed on, and a no-op
    // while toId is still "" on this very first call (nothing to flush
    // yet) — it only ever matters, and only ever saves, on a genuinely
    // completable pair, so it's safe to call unconditionally.
    selectConnectionTarget(toId: string) {
      if (!this.connectionEditor || !this.tourNodes.some((n: TourNode) => n.id === toId)) return;
      this._onConnectionPanePlaced();
      this.connectionEditor = { ...this.connectionEditor, toId };
      this._mountToPane(this.connectionEditor.fromId, toId);
      // Swapping the target changes the edited PAIR, so the FROM pane's active
      // (placement) marker — which points at the OLD target — is now stale.
      // Refresh JUST its markers (not a remount: that would re-seed its pose
      // and clobber a live placement) so its pulsing pin tracks the new pair.
      this._refreshPaneMarkers(this._connPaneFrom, this.connectionEditor.fromId, toId);
    },

    // selectConnectionFrom (Q2) is the twin of selectConnectionTarget: swaps
    // which room the FROM pane shows, driven by the always-visible
    // "from room" dropdown or the existing-links jump list
    // (openConnectionEditor is used there instead when BOTH sides of the
    // pair change at once — this is only for swapping fromId in place).
    // Flushes any pending placement via _onConnectionPanePlaced() BEFORE
    // swapping — a safety net mirroring closeConnectionEditor's own
    // re-save-then-teardown pattern: under S4 every click already autosaves
    // immediately via onPlace, so in the common case this is a cheap no-op,
    // but it guards the one gap per-click autosave can't cover on its own —
    // an add-flow "from" arrow placed before a target exists
    // (_onConnectionPanePlaced no-ops until toId is set, see its own doc
    // comment) that would otherwise be silently discarded by the pane
    // teardown/remount below if the operator changes rooms before ever
    // choosing a target. Remounts the "from" pane and refreshes the "to"
    // pane's MARKERS in place (its active marker points back at the from-room,
    // so it must re-render for the new pair — but a full remount would re-seed
    // its pose and clobber a live placement, so we only swap its markers).
    selectConnectionFrom(fromId: string) {
      if (!this.connectionEditor || !this.tourNodes.some((n: TourNode) => n.id === fromId)) return;
      this._onConnectionPanePlaced();
      this.connectionEditor = { ...this.connectionEditor, fromId };
      this._mountFromPane(fromId, this.connectionEditor.toId);
      // Swapping the from-room changes the edited PAIR, so the TO pane's active
      // (placement) marker — which points BACK at the old from-room — is now
      // stale. Refresh JUST its markers (not a remount: that would re-seed its
      // pose and clobber a live placement) so its pulsing pin tracks the pair.
      this._refreshPaneMarkers(this._connPaneTo, this.connectionEditor.toId, fromId);
    },

    // _refreshPaneMarkers re-renders a pane's marker set in place (no remount,
    // so the live viewer pose survives) after the edited PAIR changed on the
    // OTHER pane's dropdown. `paneNodeId` is the room the pane shows;
    // `editedTargetId` is the other room now being linked — its pin becomes the
    // active (pulsing __placement__) marker, seeded from the saved link between
    // them if one exists. No-op when the pane isn't mounted.
    _refreshPaneMarkers(pane: LinkPane | null, paneNodeId: string, editedTargetId: string) {
      if (!pane) return;
      const node = this.tourNodes.find((n: TourNode) => n.id === paneNodeId);
      const link = node?.links.find((l) => l.target === editedTargetId);
      const existing = link ? { yaw: link.yaw, pitch: link.pitch } : undefined;
      pane.setMarkers(this._connMarkersFor(paneNodeId, editedTargetId, existing));
    },

    // _onConnectionPanePlaced is BOTH panes' onPlace callback (S4 — no
    // "Save link" button by design): every arrow placement/adjustment
    // commits immediately via saveConnection, which itself schedules the
    // debounced full-graph autosave (_scheduleTourSave) — the SAME path
    // every other tour mutation uses, so a burst of clicks across both
    // panes still coalesces into one PUT. A no-op until a target is chosen
    // (add-flow: placing the "from" arrow before a target exists has
    // nothing to save yet — closeConnectionEditor/selectConnectionTarget
    // pick this up once a target lands). Reads each pane's last-clicked pose (getPose),
    // falling back to its mount-time seed for whichever side hasn't been
    // clicked yet (mirrors closeConnectionEditor's own fallback) — so
    // clicking only ONE side still commits a full, valid pair right away
    // instead of waiting for the operator to also touch the other pane.
    _onConnectionPanePlaced() {
      const editor = this.connectionEditor;
      if (!editor || !editor.toId) return;
      const poseFrom = this._connPaneFrom?.getPose() || this._connSeedFrom || { yaw: 0, pitch: 0 };
      const poseTo = this._connPaneTo?.getPose() || this._connSeedTo || { yaw: 0, pitch: 0 };
      this.saveConnection(editor.fromId, editor.toId, poseFrom, poseTo);
    },

    // saveConnection writes a hotspot link in BOTH directions: poseFrom is
    // where in the FROM room's own pano the operator placed the hotspot
    // leading to `toId`; poseTo is where in the TO room's own pano they
    // placed the hotspot leading back to `fromId`. Updates an existing link
    // in place (no duplicate on re-save) rather than always pushing.
    // Schedules the debounced autosave — every connection edit autosaves,
    // same as every other tour mutation.
    saveConnection(
      fromId: string,
      toId: string,
      poseFrom: { yaw: number; pitch: number },
      poseTo: { yaw: number; pitch: number },
    ) {
      if (fromId === toId) return; // no self-links
      const a = this.tourNodes.find((n: TourNode) => n.id === fromId);
      const b = this.tourNodes.find((n: TourNode) => n.id === toId);
      if (!a || !b) return;
      const forward = a.links.find((l) => l.target === toId);
      if (forward) {
        forward.yaw = poseFrom.yaw;
        forward.pitch = poseFrom.pitch;
      } else {
        a.links.push({ target: toId, yaw: poseFrom.yaw, pitch: poseFrom.pitch });
      }
      const back = b.links.find((l) => l.target === fromId);
      if (back) {
        back.yaw = poseTo.yaw;
        back.pitch = poseTo.pitch;
      } else {
        b.links.push({ target: fromId, yaw: poseTo.yaw, pitch: poseTo.pitch });
      }
      this._scheduleTourSave();
    },

    // removeConnection removes a hotspot link in BOTH directions (a one-way
    // link would leave one side's marker pointing at a room with no way
    // back, and would drift from saveConnection's always-both-directions
    // contract). Schedules the debounced autosave.
    removeConnection(aId: string, bId: string) {
      const a = this.tourNodes.find((n: TourNode) => n.id === aId);
      const b = this.tourNodes.find((n: TourNode) => n.id === bId);
      if (a) a.links = a.links.filter((l) => l.target !== bId);
      if (b) b.links = b.links.filter((l) => l.target !== aId);
      this._scheduleTourSave();
    },

    // closeConnectionEditor is the "← Zurück" handler (Q5). Placement no
    // longer waits for dismissal to commit (S4): each pane's onPlace already
    // fired _onConnectionPanePlaced on every click, which ran saveConnection
    // with this exact same read-both-poses logic. So this call is now a
    // SAFETY NET, not the primary commit path — a cheap no-op re-save in the
    // common case (nothing changed since the last click), and the only
    // commit for the one case per-placement autosave can't cover: an
    // edit-mode open where the operator never clicked either pane at all
    // (e.g. opened to inspect, then just backed out — still fine, since
    // saveConnection is idempotent against the unchanged seed pose). Reads
    // each pane's current placement via getPose() (falling back to the pose
    // it was SEEDED with when the operator never clicked — see the Q3
    // controller note on async mounts, getPose() legitimately returns null
    // until a placement happens) and commits it via saveConnection, THEN
    // tears both panes down. No target chosen yet (toId still "") → tears
    // down without saving, nothing to commit.
    closeConnectionEditor() {
      const editor = this.connectionEditor;
      if (editor && editor.toId) {
        const poseFrom = this._connPaneFrom?.getPose() || this._connSeedFrom || { yaw: 0, pitch: 0 };
        const poseTo = this._connPaneTo?.getPose() || this._connSeedTo || { yaw: 0, pitch: 0 };
        this.saveConnection(editor.fromId, editor.toId, poseFrom, poseTo);
      }
      this._teardownConnectionEditor();
      this.connectionEditor = null;
    },

    // _teardownConnectionEditor releases both panes' WebGL contexts WITHOUT
    // saving — a bare discard. Called from every path that dismisses the
    // connections editor or the whole tour dialog WITHOUT wanting a commit:
    // closeConnectionEditor (after it has already saved, if applicable), a
    // fresh openConnectionEditor (destroy-first), open()/openTour() (fresh
    // modal open), removeRoom (the room backing the open editor no longer
    // exists — nothing valid to save), the "Verbindung entfernen" button
    // (the link was just explicitly deleted — re-saving from the panes'
    // poses would resurrect it), and destroy() (section swap / modal
    // re-init). NOTE: close() and the dialog's own @close handler do NOT
    // call this directly anymore — they route through closeConnectionEditor()
    // instead, which commits any placed connection THEN calls this. Safe to
    // call when nothing is mounted.
    _teardownConnectionEditor() {
      this._connPaneFrom?.destroy();
      this._connPaneFrom = null;
      this._connPaneTo?.destroy();
      this._connPaneTo = null;
      this._connSeedFrom = null;
      this._connSeedTo = null;
    },

    // tourPoolFiltered narrows the tour room-picker grid to pano images only
    // (a plain photo can't anchor a 360° room) unless the operator flips
    // showAllInTour — mirrors the pano-crop takeover's own "only panos are
    // eligible" framing, but as an explicit toggle rather than a hard filter,
    // since a wanted image may not be classified as a pano yet. Also applies
    // the SAME `q` text filter as filteredPool() (Q6) — the top search box
    // otherwise silently did nothing in tour mode.
    tourPoolFiltered(): PoolImage[] {
      const base = this.visiblePool;
      const kindFiltered = this.showAllInTour ? base : base.filter((p: PoolImage) => p.media_kind === "pano");
      const q = this.q.trim().toLowerCase();
      if (!q) return kindFiltered;
      return kindFiltered.filter((p: PoolImage) => this._matchesSearch(p, q));
    },

    // _scheduleTourSave debounces (~600ms) a full-graph PUT — every tour
    // mutation (addRoom/removeRoom/roomName/setStartRoom/reorder/
    // saveConnection/removeConnection) calls this instead of saving
    // directly, so a burst of edits coalesces into ONE SaveTour (one audit
    // event, one cache-invalidation — see the task brief).
    _scheduleTourSave() {
      this._tourDirty = true;
      if (this._tourSaveTimer) clearTimeout(this._tourSaveTimer);
      this._tourSaveTimer = setTimeout(() => {
        this._tourSaveTimer = null;
        this._runTourSave();
      }, 600);
    },

    // flushTourSave cancels any pending debounce timer and, if there's
    // unsaved work AND no save is already in flight, runs it immediately.
    // Called from close() (tour mode has no separate Save button — Q5's
    // "Fertig" IS close()) and from the htmx:before-request/beforeunload
    // listeners wired in init() — a boosted nav or hard reload doesn't wait
    // for a setTimeout. If a save IS already in flight, flush is a no-op:
    // _runTourSave's own completion picks up _tourDirty and fires the
    // follow-up — see the "at most one PUT in flight" contract there.
    //
    // Retry-on-close: _runTourSave() clears _tourDirty at SAVE-START, before
    // it knows whether the PUT will succeed — so a failed save leaves
    // tourSaveState "error" with _tourDirty already false. Without forcing
    // dirty back on here, close()->flushTourSave() right after a failure
    // would be a no-op and the graph would be silently dropped on dismissal.
    // Force exactly one more attempt whenever the last known outcome was a
    // failure; a further failure just re-sets "error" (no in-memory data is
    // ever lost — see _runTourSave's own doc comment).
    flushTourSave() {
      if (this._tourSaveTimer) {
        clearTimeout(this._tourSaveTimer);
        this._tourSaveTimer = null;
      }
      if (this.tourSaveState === "error") this._tourDirty = true;
      if (this._tourDirty && !this._tourSaving) {
        this._runTourSave();
      }
    },

    // _runTourSave PUTs the full graph via the shared saveTourGraph()
    // (tour-api.ts). Serialized: keeps AT MOST ONE PUT in flight — a second
    // call while `_tourSaving` is true is a no-op (the flag guards it), and
    // a mutation that lands mid-save (_scheduleTourSave setting _tourDirty
    // again) is picked up by the `if (this._tourDirty) this._runTourSave()`
    // at the end, firing EXACTLY ONE immediate follow-up once the current
    // PUT resolves — never an overlapping call. On failure the in-memory
    // graph is left untouched and tourSaveState becomes "error"; no
    // automatic retry — the next mutation's own _scheduleTourSave call is
    // what retries (dirty is only set by real mutations, never by a failure
    // alone).
    async _runTourSave() {
      if (this._tourSaveTimer) {
        clearTimeout(this._tourSaveTimer);
        this._tourSaveTimer = null;
      }
      if (this._tourSaving || !this._tourDirty) return;
      this._tourDirty = false;
      this._tourSaving = true;
      this.tourSaveState = "saving";
      const graph: TourGraph = {
        start_node_id: this.tourStartId || this.tourNodes[0]?.id || "",
        // Re-index by array position: drag-reorder permutes tourNodes but
        // never rewrites each node's own `order` field, so without this the
        // public viewer's rail (which sorts by `order`) would ignore CMS
        // reordering entirely.
        nodes: this.tourNodes.map((n, i) => ({ ...n, order: i })),
      };
      const dlg = (this as any).$refs?.dlg as HTMLElement | undefined;
      const ok = await saveTourGraph(this._tourBaseUrl(), graph, dlg?.dataset?.tourSaveFailed);
      this._tourSaving = false;
      this.tourSaveState = ok ? "saved" : "error";
      if (this._tourDirty) {
        this._runTourSave();
      }
    },
  };
}
